<template>
  <transition name="landscape-fade">
    <div v-if="isLandscape" class="landscape-tip">
      <div class="tip-content">
        <div class="tip-icon">
          <img src="@/assets/iphone.png" />
        </div>
        <div class="tip-text">{{ t("common.landscapeTip") }}</div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { isMobileLandscape } from "@/utils/index";

const { t } = useI18n();

const isLandscape = ref(false);
let orientationTimer: ReturnType<typeof setTimeout> | null = null;

// 检测屏幕方向
const checkOrientation = () => {
  isLandscape.value = isMobileLandscape();
};

// 监听屏幕方向变化
const handleOrientationChange = () => {
  // 清除之前的定时器，避免重复执行
  if (orientationTimer !== null) {
    clearTimeout(orientationTimer);
  }
  // 延迟一下，等待屏幕方向变化完成
  orientationTimer = setTimeout(() => {
    checkOrientation();
    orientationTimer = null;
  }, 100);
};

// 监听窗口大小变化（作为备用方案）
const handleResize = () => {
  checkOrientation();
};

onMounted(() => {
  checkOrientation();
  // 监听屏幕方向变化事件
  window.addEventListener("orientationchange", handleOrientationChange);
  // 监听窗口大小变化（某些浏览器可能不支持 orientationchange）
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  // 清理定时器，防止组件卸载后访问已销毁的响应式变量
  if (orientationTimer !== null) {
    clearTimeout(orientationTimer);
    orientationTimer = null;
  }
  window.removeEventListener("orientationchange", handleOrientationChange);
  window.removeEventListener("resize", handleResize);
});
</script>

<style scoped>
.landscape-tip {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.tip-content {
  background: rgba(34, 34, 34, 0.95);
  border-radius: 12px;
  padding: 24px 32px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  max-width: 80%;
}

.tip-icon {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tip-icon img {
  width: 64px;
  height: 64px;
  animation: rotate 2s ease-in-out infinite;
}

.tip-text {
  color: #fff;
  font-size: 16px;
  line-height: 1.5;
}

@keyframes rotate {
  0%,
  100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(-90deg);
  }
  75% {
    transform: rotate(90deg);
  }
}

.landscape-fade-enter-active,
.landscape-fade-leave-active {
  transition: opacity 0.3s ease;
}

.landscape-fade-enter-from,
.landscape-fade-leave-to {
  opacity: 0;
}
</style>
